
import { AuthService } from "@/services/auth-service";
import { Company } from "@/models/company";
import { CompanyService } from "@/services/company-service";
import Vue from "vue";
import { Route } from "vue-router";
import { IDataTableColumn } from "winfakt-vue-components";
import { setTitle, resetTitle } from "@/utils/title";
import AppCompanyFilters from "@/components/company/CompanyFilters.vue";
import { User } from "@/models/user";
import AppFetchingSpinnerOverlay from "@/components/FetchingSpinnerOverlay.vue";
import { CompanyFilters } from "@/utils/company-filters";
import { MetricService } from "@/services/metric-service";
import { DbInfo } from "@/models/db-info";

interface IDataTableColumnExtended extends IDataTableColumn {
	dbName?:string;
}

export default Vue.extend({
	data() {
		return {
			companies: [] as Company[],
			selectedItems: [] as Company[],
			total: 0,
			limit: 20,
			page: 1,
			orderColumn: "this.ID",
			orderDirection: false,
			searchInput: "",
			ready: false,
			currentQueryId: 0.0,
			filtersOpened: false,
			filters: new CompanyFilters(),
			isFetching: false,
			downloadingCsv: false,
			allDbs: [] as DbInfo[]
		};
	},
	computed: {
		companyId(): number | null{
			if(this.selectedItems.length){
				return this.selectedItems[0].id;
			}
			return null;
		},
		// eslint-disable-next-line max-lines-per-function
		columns():IDataTableColumnExtended[] {
			let _t = this;
			return [
				{
					name: "ID",
					property: "id",
					width: "50px",
					click(item:Company) {
						_t.$router.push(`/companies/${item.id}/edit`);
					},
					orderColumnName: "this.ID"
				},
				{
					name: "Klant",
					property: "name",
					width: "200px",
					orderColumnName: "this.Name"
				},
				{
					name: "Editie",
					property: "tierAsString",
					width: "150px",
					disableSorting: true,
				},
				{
					name: "Email",
					width: "250px",
					property: "owner",
					disableSorting: true,
					print(value:User):string{
						if (!value) return "UNDEFINED";
						return value.email;
					}
				},
				{
					name: "Actief tot",
					property: "activeUntil",
					print: Company.getActiveUntilString,
					disableSorting: false,
				},
				{
					name: "Service tot",
					property: "serviceUntilString",
					disableSorting: true,
				},
				{
					name: "WF Offline klnr.",
					property: "WfOfflineID",
					width: "100px",
					disableSorting: false
				},
				{
					name: "Eerste Aankoop",
					property: "dateFirstActivation",
					width: "200px",
					print(value:Date):string{
						if (!value) return "";
						return value.toLocaleDateString("nl-be");;
					}
				},
				{
					name: "Database",
					property: "DbID",
					width: "100px",
					print(value:number):string{
						let foundDb = _t.allDbs.find(a=>a.id == value);
						if (foundDb) {
							return foundDb.toString();
						}
						return `${value+1}`;
					}
				}
			] as IDataTableColumnExtended[];
		},
		editDisabled():boolean {
			return this.selectedItems.length != 1;
		},
		selectedItem():Company {
			return this.selectedItems[0];
		},
		editLink():string {
			if (this.editDisabled) {
				return "#";
			}
			return this.createEditLink(this.selectedItems[0].id);
		},
		showInfoTooltTip():string {
			return"View Account Info";
		},
		showInfoLink():string {
			if (this.editDisabled) {
				return "#";
			}
			return this.createShowInfoLink(this.selectedItems[0].id);
		},
		limits():any[] {
			let limits = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500];
			return limits.map(l => {
				return {
					text: "Limit " + l + " records",
					value: l
				};
			});
		},
		pageCount():number {
			return this.total / this.limit;
		},
		hasFilters():boolean {
			return this.filtersToString(this.filters) != this.filtersToString(new CompanyFilters());
		},
		showTotalRecordsText():string {
			return "Aantal lijnen: " + this.total;
		},
		isAdmin():boolean {
			if (!AuthService.user) return false;
			return AuthService.user.hasPermission("admin");
		}
	},
	watch: {
		limit() {
			if (this.pageCount > this.page){
				this.page = this.pageCount;
			}
			this.getCompanies();
		},
		page() {
			this.getCompanies();
		},
		orderColumn() {
			this.getCompanies();
		},
		orderDirection() {
			this.getCompanies();
		},
		"filters.searchQuery"(){
			let id = Math.random();
			this.currentQueryId = id;
			setTimeout(()=>{
				if (this.currentQueryId != id) return;
				this.getCompanies();
			}, 500);
		},
		filters(){
			this.getCompanies();
		}
	},
	methods: {
		filtersToString(filters:CompanyFilters):string{
			let accountStatusKeys = Object.keys(filters.accountStatus);
			let accountStatusArray = accountStatusKeys.sort((a,b)=>a.localeCompare(b)).map(k=>(filters.accountStatus as any)[k]);
			let keys = Object.keys(filters) as (keyof CompanyFilters)[];
			let array = keys.sort((a,b)=>a.localeCompare(b)).map(k=>{
				if (k == "accountStatus"){
					return accountStatusArray;
				}
				return filters[k];
			});
			return JSON.stringify(array);
		},
		async getCompanies() {
			if (!this.ready) return;
			let id = Math.random();
			this.currentQueryId = id;
			this.setQuery();
			let orderColumn = this.columns.find(c => (c.orderColumnName || c.property) == this.orderColumn) || {dbName: undefined};
			this.isFetching = true;
			let response = await CompanyService.getCompanies(this.limit, this.page, (orderColumn as any).dbName || this.orderColumn, this.orderDirection, this.filters);
			if (this.currentQueryId != id) return;
			this.isFetching = false;
			this.companies = response.companies;
			this.total = response.total;
			if (this.page != 1 && this.companies.length == 0){
				this.page = 1;
				await this.getCompanies();
			}
		},
		setQuery(){
			let path = `/companies?limit=${this.limit}&page=${this.page}&orderColumn=${this.orderColumn}&orderDirection=${this.orderDirection}&searchInput=${this.searchInput}`;
			if ((this.$route as Route).fullPath != path){
				this.$router.push(path);
			}
		},
		onOrder(event:any) {
			this.orderColumn = event.order;
			this.orderDirection = event.ascending;
		},
		async deleteCompany() {
			let confirm = await this.$wf.confirm(`Are you sure you want to delete ${this.selectedItem.name}?`, "yes", "no");
			if (confirm) {
				await CompanyService.deleteCompany(this.selectedItem);
				this.getCompanies();
			}
			this.selectedItems = [];
		},
		setPage(value:number){
			this.page = Math.round(value);
		},
		removeFilters(){
			this.filters = new CompanyFilters();
		},
		createEditLink(compId:number):string{
			return `/companies/${compId}/edit`;
		},
		createShowInfoLink(compId:number):string{
			return `/companies/${compId}/info`;
		},
		edit(item:Company){
			this.$router.push(this.createEditLink(item.id));
		},
		onButtonClickShowInfo(item:Company){
			this.$router.push(this.createShowInfoLink(item.id));
		},
		hasPermission(permission:string):boolean{
			if (!AuthService.user){
				return false;
			}
			return AuthService.user.hasPermission(permission);
		},
		async downloadExport(){
			this.downloadingCsv = true;
			try{
				await CompanyService.downloadExport(this.filters);
			}catch(err){
			}
			this.downloadingCsv = false;
		}
	},
	async created(){
		this.allDbs = await MetricService.getAllDbIds();
		let viewSettings = await CompanyService.getViewSettings();
		this.limit = viewSettings.limit;
		this.orderColumn = viewSettings.orderColumn;
		this.orderDirection = viewSettings.orderAscending;
		this.filters = await CompanyService.getStoredFilters();
		if (this.$route.query.limit){
			this.limit = parseInt(this.$route.query.limit as string) || this.limit;
		}
		if (this.$route.query.page){
			this.page = parseInt(this.$route.query.page as string) || 1;
		}
		if (this.$route.query.orderColumn){
			this.orderColumn = this.$route.query.orderColumn as string;
		}
		if (this.$route.query.orderDirection){
			this.orderDirection = this.$route.query.orderDirection == "true";
		}
		if (this.$route.query.searchInput){
			this.searchInput = this.$route.query.searchInput as string;
		}
		if (this.orderColumn == "") {
			this.orderColumn = "id";
		}
		if (this.limit == 0){
			this.limit = 25;
		}
		if (this.page == 0){
			this.page = 1;
		}

		setTitle("Companies");
		requestAnimationFrame(()=>{
			this.ready = true;
			this.getCompanies();
		});
	},
	destroyed(){
		resetTitle();
	},
	components: {
		AppCompanyFilters,
		AppFetchingSpinnerOverlay
	}
});
